import React, { useState, useRef, useEffect } from 'react';
import axios, { AxiosProgressEvent } from 'axios';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { format } from 'date-fns';
'use client'
import { PitchDeckUpload } from './PitchDeckUpload'
import { AnalysisHistory } from './AnalysisHistory'
import { DashboardHeader } from './DashboardHeader'
import { BackgroundPattern } from '@/components/ui/background-pattern'


interface UploadedFile {
    id: string;
    name: string;
    size: number;
    uploaded_at: string;
    status: string;
}

const Dashboard: React.FC = () => {
    const [file, setFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [apiStatus, setApiStatus] = useState<string>('');
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [filesLoading, setFilesLoading] = useState(true);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            // Check file size
            if (selectedFile.size > config.MAX_FILE_SIZE) {
                alert('File size must be less than 10MB');
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }
            // Check file type
            const fileExtension = '.' + selectedFile.name.split('.').pop()?.toLowerCase();
            if (!config.ALLOWED_FILE_TYPES.includes(fileExtension)) {
                alert('Only PDF, PPT, and PPTX files are allowed');
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }
            setFile(selectedFile);
        }
    };

    return (
        <div className="relative min-h-screen">
            <BackgroundPattern />
            <div className="relative">
                <main className="min-h-screen bg-transparent">
                    <div className="container mx-auto py-8 px-4">
                        <h1 className="text-5xl font-extrabold mb-6 tracking-tight">
                            <span className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-400 bg-clip-text text-transparent">
                                Pitch Deck Analysis
                            </span>
                        </h1>
                        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed mb-12">
                            Transform your pitch deck with our{' '}
                            <span className="text-indigo-600 dark:text-indigo-400 font-semibold">AI-powered analysis</span>.
                            Get detailed feedback and insights to make your presentation{' '}
                            <span className="text-indigo-600 dark:text-indigo-400 font-semibold">investor-ready</span>.
                        </p>

                        <div className="flex flex-col items-center">
                            <div className="w-full max-w-3xl mx-auto">
                                <PitchDeckUpload 
                                    onUploadSuccess={(response) => {
                                        const { analysis_id } = response;
                                        if (analysis_id) {
                                            navigate(`/analysis/${analysis_id}`);
                                        } else {
                                            console.error('No analysis_id in response:', response);
                                        }
                                    }}
                                    onUploadError={(error) => {
                                        console.error('Upload error:', error);
                                        if (error.response?.status === 401) {
                                            navigate('/login');
                                        }
                                    }}
                                />
                            </div>
                            <div className="w-full max-w-3xl mx-auto mt-8">
                                <AnalysisHistory />
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Dashboard;
